import API_URL from "../config/api";
import {
  isAdminDashboardToken,
  shouldDeferUnauthorizedRecovery,
} from "../utils/impersonation";
import {
  clearAuthAndImpersonationForLogin,
  recoverStoredAdminSession,
} from "../utils/impersonationRecovery";

export const buildQueryString = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();

  return queryString ? `?${queryString}` : "";
};

const readAuthToken = () => localStorage.getItem("authToken");

const handleUnauthorized = async ({ baseURL = API_URL } = {}) => {
  if (shouldDeferUnauthorizedRecovery()) return;

  const activeToken = localStorage.getItem("authToken");

  if (isAdminDashboardToken(activeToken)) return;

  const restored = await recoverStoredAdminSession({ baseURL }).catch(
    () => null
  );

  if (restored) return;

  if (shouldDeferUnauthorizedRecovery()) return;

  clearAuthAndImpersonationForLogin();
};

export class DashboardHttpClient {
  constructor({
    baseURL = API_URL,
    getToken = readAuthToken,
    onUnauthorized = handleUnauthorized,
  } = {}) {
    this.baseURL = baseURL;
    this.getToken = getToken;
    this.onUnauthorized = onUnauthorized;
  }

  async request(endpoint, options = {}) {
    const token = this.getToken();
    const config = {
      ...options,
      headers: {
        ...options.headers,
      },
    };

    if (options.body || ["POST", "PUT", "PATCH"].includes(options.method)) {
      config.headers["Content-Type"] = "application/json";
    }

    if (
      (options.method === "POST" ||
        options.method === "PUT" ||
        options.method === "PATCH") &&
      !options.body
    ) {
      config.body = JSON.stringify({});
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const response = await fetch(`${this.baseURL}${endpoint}`, config);

    if (!response.ok) {
      if (response.status === 401) {
        await this.onUnauthorized({
          baseURL: this.baseURL,
          endpoint,
          response,
        });
      }

      const errorData = await response.json().catch(() => ({
        error: `API Error: ${response.statusText}`,
      }));
      const error = new Error(
        errorData.error || `API Error: ${response.statusText}`
      );

      error.response = { data: errorData };
      error.details = errorData.details;
      throw error;
    }

    if (response.status === 204) return null;

    return response.json();
  }
}

const httpClient = new DashboardHttpClient();

export default httpClient;
